export const careerPathsFaq = [
    {
        id: 1,
        title: 'What is a Career Path and how is it different from taking single courses?',
        description: 'A Career Path is a curated sequence of 5–7 courses arranged in a logical order for a specific role in the pharmaceutical industry. You can still complete individual courses on their own, but a Career Path gives you a structured, standard learning route so you build skills step by step rather than in a scattered way.',
    },
    {
        id: 2,
        title: 'How do I know which Career Path is right for me?',
        description: 'Each Career Path includes a description of the role, target profile, and the skills you will gain. You can also take the free pre assessment for that path to identify your strengths and gaps and see recommended courses. If you’re unsure, our support team can help you choose via email.',
    },
    {
        id: 3,
        title: 'Can I enroll in more than one Career Path at the same time?',
        description: 'Yes. As long as your subscription is active, there is no limit to the number of Career Paths or courses you can follow in parallel. For better time management, we recommend focusing on one or two paths at a time.',
    },
    {
        id: 4,
        title: 'What language are the Career Paths delivered in? Do you provide subtitles or transcripts?',
        description: 'All courses are delivered in clear, easy to follow English. At the moment we do not provide subtitles or written transcripts.',
    },
    {
        id: 5,
        title: 'Can I keep access to the Career Path materials after my subscription ends?',
        description: 'You have full access to all course content and downloadable materials for as long as your subscription is active. Once your subscription expires, access to the platform content stops, but anything you have already downloaded remains yours.',
    },
    {
        id: 6,
        title: 'How long does each Career Path take, and is there any time limit to complete it?',
        description: 'Our courses are fully flexible and do not have a fixed completion deadline. You are free to set your own pace and revisit the content whenever you need, as long as your subscription remains active.',
    },
    {
        id: 7,
        title: 'What levels are the courses offered at?',
        description: 'Courses are offered across three levels—beginner, intermediate, and advanced—so they cater both to newcomers and experienced professionals. The recommended level and any prerequisites are indicated on each course page.',
    },
    {
        id: 8,
        title: 'Which regulations and standards do the Career Paths cover?',
        description: 'Our curriculum spans key areas such as GMP, Pharmacovigilance, Validation, Quality Control, and Quality Assurance. Within these, we aim to cover all major international regulations and guidelines relevant to each topic; specific references are listed in the individual course descriptions.',
    },
    {
        id: 9,
        title: 'How often is the content updated?',
        description: 'Our education team continuously reviews and updates courses and assessments to reflect the latest regulatory changes and best practices. Whenever a course is updated, the new version automatically becomes available to all learners with an active subscription.',
    },
    {
        id: 10,
        title: 'What are the assessments for Career Paths and how do they work?',
        description: 'Each Career Path includes a dedicated assessment designed to evaluate your knowledge across all key aspects of that specialty. The assessment is free, entirely optional, and can be taken as many times as you wish; it includes a mix of question types (e.g., multiple choice, scenario based) to give you a well rounded skills snapshot.',
    },
    {
        id: 11,
        title: 'What certificates do I receive after completion?',
        description: 'You receive a certificate upon completing each course, and an additional certificate when you complete an entire Career Path. Certificates include full details such as course/path title, scope, approximate workload, and level, and can be easily shared on your CV or LinkedIn.',
    },
    {
        id: 12,
        title: 'How can I contact support or request a team / corporate offer?',
        description: 'For any technical questions, learning advice, or organizational enrollments, you can reach us via our main support email. Our team will also provide tailored offers and conditions for companies and teams.',
    },
    {
        id: 13,
        title: 'What subscription plans do you offer?',
        description: 'We offer monthly and yearly subscriptions in two tiers:<ul><li><strong>Plus</strong> – full access to all learning content, courses, and Career Paths.</li><li><strong>Pro</strong> – everything in Plus, plus 1:1 coaching, interview preparation, and career consulting.</li></ul>',
    },
];

export const toolkitsFaq = [
    {
        id: 1,
        title: 'What is the Pharmuni Toolkit?',
        description: 'The Pharmuni Toolkit is a curated library of practical resources designed to help pharmaceutical professionals work faster and stay compliant. It includes ready-to-use SOP templates, calculators, checklists, and quick guides aligned with GMP and industry standards.',
    },
    {
        id: 2,
        title: 'Who are the tools designed for?',
        description: 'Our tools are designed for everyone working in the pharmaceutical industry — from QA and QC specialists to manufacturing, regulatory, and operations teams. Whether you are new to a role or an experienced professional, the toolkit helps you save time and reduce documentation effort.',
    },
    {
        id: 3,
        title: 'Are the tools free or do I need a subscription?',
        description: 'Some tools are completely free and can be downloaded immediately. Others are available either through a one-time purchase or as part of a Pharmuni Pro subscription, which gives you full access to the entire toolkit library.',
    },
    {
        id: 4,
        title: 'What is the difference between buying a single tool and subscribing to Pharmuni Pro?',
        description: 'Buying a single tool gives you lifetime access to that specific resource. Subscribing to Pharmuni Pro unlocks the entire toolkit library plus all Pharmuni courses, career paths, and learning resources for as long as your subscription is active.',
    },
    {
        id: 5,
        title: 'In what formats are the tools provided?',
        description: 'Most tools are delivered as editable files (e.g., Word documents, Excel spreadsheets, or PDFs) so you can adapt them to your company\'s specific needs. The exact format is shown on each tool page before you download.',
    },
    {
        id: 6,
        title: 'Are the tools compliant with GMP and other regulations?',
        description: 'Yes. All tools are developed and reviewed by experienced pharma professionals and aligned with GMP guidelines and relevant international standards. However, you should always adapt them to your company\'s internal procedures and applicable local regulations.',
    },
    {
        id: 7,
        title: 'How often are the tools updated?',
        description: 'Our team regularly reviews and updates the toolkit to reflect the latest regulatory changes and industry best practices. The most recent update date is shown on each tool page.',
    },
    {
        id: 8,
        title: 'Can I keep the tools I have purchased after my subscription ends?',
        description: 'Yes. Tools you purchased as a one-time payment remain yours forever, regardless of your subscription status. Tools accessed through a Pharmuni Pro subscription are available as long as your subscription is active, but anything you have already downloaded remains on your device.',
    },
];

export const assessmentsFaq = [
    {
        id: 1,
        title: 'What are these assessments and why should I take them?',
        description: 'Our assessments are short online tests designed to measure your skills in specific pharma and GMP roles. They help you understand your current level, identify knowledge gaps, and see how you compare to other professionals. You can then use the results to plan your learning and strengthen your CV.',
    },
    {
        id: 2,
        title: 'Are the assessments free, or do I need a subscription?',
        description: 'All assessments on this page are completely free to take. You do not need a paid subscription or course purchase to access them.',
    },
    {
        id: 3,
        title: 'Do I need an account to take an assessment?',
        description: 'Yes. You just need to create a free account with your email. Sign-up is quick and only takes a moment, and it allows us to save your results and show your progress over time.',
    },
    {
        id: 4,
        title: 'How long does an assessment take, and how many questions are there?',
        description: 'Each assessment has a fixed number of questions (for example, 24–35 questions) and typically takes around 20–30 minutes to complete. You’ll see the approximate time and question count on each assessment card before you start.',
    },
    {
        id: 5,
        title: 'What exactly will I get in my assessment results?',
        description: 'You’ll see:<ul><li>Your overall score</li><li>How you rank compared to other participants</li><li>Your scores in each category/topic covered in the assessment</li><li>A list of recommended courses to help you close gaps and strengthen your profile</li></ul>',
    },
    {
        id: 6,
        title: 'Will you recommend courses or a career path based on my results?',
        description: 'Yes. Based on your performance and category scores, we suggest relevant courses (and where applicable, career paths) that can help you improve your skills and boost your CV.',
    },
    {
        id: 7,
        title: 'What language are the assessments in, and how good should my English be?',
        description: 'All assessments are in English. You don’t need to be perfect, but you should be comfortable reading professional and technical English related to pharmaceutical and GMP topics.',
    },
    {
        id: 8,
        title: 'Can I retake an assessment? Is there a limit?',
        description: 'Yes, you can retake an assessment if you want to improve your score or see your progress. We don’t strictly limit retakes, but we recommend leaving some time between attempts so you can actually learn and improve.',
    },
    {
        id: 9,
        title: 'Am I required to buy a course or subscription after taking an assessment?',
        description: 'No. Taking an assessment does not create any obligation to buy anything. You can simply view your results and recommendations and decide for yourself what to do next.',
    },
    {
        id: 10,
        title: 'Will I receive a certificate after completing an assessment?',
        description: 'At the moment, assessments are designed for self-evaluation and guidance, not certification. You do not receive an official certificate for completing them.',
    },
    {
        id: 11,
        title: 'Are my assessment results shared with companies or anyone else?',
        description: 'No. Your results are only visible to you inside your account. We do not share individual assessment scores with employers, HR teams, or third parties.',
    },
    {
        id: 12,
        title: 'What happens if I leave an assessment halfway through?',
        description: 'If you close the browser or lose connection, your progress may not be fully saved. For the most accurate score, we recommend completing the assessment in one sitting with a stable internet connection.',
    },
];
